import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Printer, MapPin, Package, Settings, Plane } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';

function PrintDeliveries({ userId, setActiveComponent }) {
  const [selectedDeliveryPoints, setSelectedDeliveryPoints] = useState([]);
  const [selectedProvinceDeliveries, setSelectedProvinceDeliveries] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [provinceDeliveries, setProvinceDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printerType, setPrinterType] = useState('a4');
  const printRef = useRef();

  useEffect(() => {
    fetchAllDeliveries();
  }, [userId]);

  async function fetchAllDeliveries() {
    setLoading(true);
    try {
      // Fetch regular delivery points
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/${userId}`);
      const allDeliveryPoints = userResponse.data.delivery_points || [];
      
      // Filter out deliveries with provider_id (those are handled by providers)
      const userOwnDeliveries = allDeliveryPoints.filter(dp => !dp.provider_id);
      setDeliveryPoints(userOwnDeliveries);

      // Fetch province deliveries
      const provinceResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/${userId}/province-deliveries`
      );
      const allProvinceDeliveries = provinceResponse.data.province_deliveries || [];
      
      // Filter out province deliveries with provider_id
      const userOwnProvinceDeliveries = allProvinceDeliveries.filter(dp => !dp.provider_id);
      setProvinceDeliveries(userOwnProvinceDeliveries);
      
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectionChange(dp, isProvince = false) {
    if (isProvince) {
      setSelectedProvinceDeliveries(prev => {
        if (prev.some(item => item.id === dp.id)) {
          return prev.filter(item => item.id !== dp.id);
        } else {
          return [...prev, dp];
        }
      });
    } else {
      setSelectedDeliveryPoints(prev => {
        if (prev.some(item => item.id === dp.id)) {
          return prev.filter(item => item.id !== dp.id);
        } else {
          return [...prev, dp];
        }
      });
    }
  }

  function handleSelectAll(isProvince = false) {
    if (isProvince) {
      if (selectedProvinceDeliveries.length === provinceDeliveries.length) {
        setSelectedProvinceDeliveries([]);
      } else {
        setSelectedProvinceDeliveries([...provinceDeliveries]);
      }
    } else {
      if (selectedDeliveryPoints.length === deliveryPoints.length) {
        setSelectedDeliveryPoints([]);
      } else {
        setSelectedDeliveryPoints([...deliveryPoints]);
      }
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: printerType === 'thermal' 
      ? `
        @page {
          size: 80mm auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `
      : `
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        @media print {
          body { margin: 0; }
        }
      `
  });

  const allLimaSelected = deliveryPoints.length > 0 && selectedDeliveryPoints.length === deliveryPoints.length;
  const allProvinciaSelected = provinceDeliveries.length > 0 && selectedProvinceDeliveries.length === provinceDeliveries.length;
  const totalSelected = selectedDeliveryPoints.length + selectedProvinceDeliveries.length;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Analytics')}
              className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat">
              Rotulado
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 border-l-4 border-brand-primary rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-brand-primary font-montserrat mb-1">
                  Selecciona las entregas a imprimir
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Escoge las entregas que deseas imprimir en formato de etiquetas
                </p>
              </div>
            </div>
          </div>

          {/* Printer Type Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="w-5 h-5 text-brand-primary" />
              <h3 className="text-base font-bold text-slate-900 font-montserrat">
                Tipo de Impresora
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setPrinterType('a4')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'a4'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'a4' ? 'border-brand-primary' : 'border-slate-300'
                  }`}>
                    {printerType === 'a4' && (
                      <div className="w-3 h-3 rounded-full bg-brand-primary"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora A4
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Papel estándar A4 (210 × 297 mm)
                  <br />
                  8 etiquetas por hoja
                </p>
              </button>

              <button
                onClick={() => setPrinterType('thermal')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'thermal'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'thermal' ? 'border-brand-primary' : 'border-slate-300'
                  }`}>
                    {printerType === 'thermal' && (
                      <div className="w-3 h-3 rounded-full bg-brand-primary"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora Térmica
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Xprinter XP-365B (80mm ancho)
                  <br />
                  1 etiqueta por rollo
                </p>
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && deliveryPoints.length === 0 && provinceDeliveries.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                No hay entregas disponibles
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                Crea una entrega para poder imprimir etiquetas
              </p>
            </div>
          )}

          {/* Lima Delivery Points Section */}
          {!loading && deliveryPoints.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-brand-primary" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                    Entregas Lima ({deliveryPoints.length})
                  </h3>
                </div>
                <button
                  onClick={() => handleSelectAll(false)}
                  className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors duration-200 font-montserrat text-sm"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                    allLimaSelected ? 'bg-brand-primary border-brand-primary' : 'border-slate-300'
                  }`}>
                    {allLimaSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-slate-700 font-medium">
                    {allLimaSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </span>
                </button>
              </div>

              <div className="space-y-3">
                {deliveryPoints.map(dp => {
                  const isSelected = selectedDeliveryPoints.some(item => item.id === dp.id);
                  return (
                    <button
                      key={dp.id}
                      onClick={() => handleSelectionChange(dp, false)}
                      className={`w-full bg-white rounded-xl border-2 transition-all duration-200 p-4 text-left ${
                        isSelected 
                          ? 'border-brand-primary shadow-md' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                          isSelected ? 'bg-brand-primary border-brand-primary' : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>

                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-brand-primary' : 'bg-slate-100'
                        }`}>
                          <MapPin className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-base font-bold text-slate-900 font-montserrat truncate">
                              {dp.recipient_name}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 font-montserrat whitespace-nowrap">
                              ID: {dp.id}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 font-montserrat line-clamp-1 mb-1">
                            {dp.address}
                          </p>
                          {dp.district && (
                            <p className="text-xs text-slate-500 font-montserrat">
                              {dp.district}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Province Delivery Points Section */}
          {!loading && provinceDeliveries.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Plane className="w-5 h-5 text-brand-gold" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                    Entregas Provincia ({provinceDeliveries.length})
                  </h3>
                </div>
                <button
                  onClick={() => handleSelectAll(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors duration-200 font-montserrat text-sm"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                    allProvinciaSelected ? 'bg-brand-gold border-brand-gold' : 'border-slate-300'
                  }`}>
                    {allProvinciaSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-slate-700 font-medium">
                    {allProvinciaSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </span>
                </button>
              </div>

              <div className="space-y-3">
                {provinceDeliveries.map(dp => {
                  const isSelected = selectedProvinceDeliveries.some(item => item.id === dp.id);
                  return (
                    <button
                      key={dp.id}
                      onClick={() => handleSelectionChange(dp, true)}
                      className={`w-full bg-white rounded-xl border-2 transition-all duration-200 p-4 text-left ${
                        isSelected 
                          ? 'border-brand-gold shadow-md' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                          isSelected ? 'bg-brand-gold border-brand-gold' : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>

                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-brand-gold' : 'bg-slate-100'
                        }`}>
                          <Plane className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-base font-bold text-slate-900 font-montserrat truncate">
                              {dp.recipient_name}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 font-montserrat whitespace-nowrap">
                              ID: {dp.id}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 font-montserrat line-clamp-1 mb-1">
                            {dp.province} - {dp.district}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat line-clamp-1">
                            {dp.address}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Fixed Footer - Print Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handlePrint}
            disabled={totalSelected === 0}
            className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            <span>
              Imprimir {totalSelected > 0 && `(${totalSelected})`}
            </span>
          </button>
        </div>
      </div>

      {/* Hidden Print Content */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {printerType === 'a4' ? (
            <A4PrintLayout 
              limaDeliveries={selectedDeliveryPoints}
              provinciaDeliveries={selectedProvinceDeliveries}
            />
          ) : (
            <ThermalPrintLayout
              limaDeliveries={selectedDeliveryPoints}
              provinciaDeliveries={selectedProvinceDeliveries}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// A4 Print Layout Component
function A4PrintLayout({ limaDeliveries, provinciaDeliveries }) {
  const allDeliveries = [
    ...limaDeliveries.map(d => ({ ...d, type: 'lima' })),
    ...provinciaDeliveries.map(d => ({ ...d, type: 'provincia' }))
  ];

  const pages = [];
  for (let i = 0; i < allDeliveries.length; i += 8) {
    const pageDeliveries = allDeliveries.slice(i, i + 8);
    pages.push(
      <div
        key={`page-${i}`}
        style={{
          width: '210mm',
          height: '297mm',
          pageBreakAfter: i + 8 < allDeliveries.length ? 'always' : 'auto',
          fontFamily: 'Montserrat, sans-serif',
          padding: '8mm',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'repeat(4, 1fr)',
          gap: '6mm',
          width: '100%',
          height: '100%'
        }}>
          {pageDeliveries.map((dp) => (
            dp.type === 'lima' ? (
              <LimaLabelA4 key={`lima-${dp.id}`} dp={dp} />
            ) : (
              <ProvinciaLabelA4 key={`provincia-${dp.id}`} dp={dp} />
            )
          ))}
        </div>
      </div>
    );
  }
  return <>{pages}</>;
}

// Lima Label for A4
function LimaLabelA4({ dp }) {
  return (
    <div
      style={{
        border: '2px solid #B1336E',
        padding: '4mm',
        boxSizing: 'border-box',
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '3mm',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <p style={{ 
        color: '#B1336E', 
        fontSize: '18px', 
        fontWeight: 'bold',
        margin: '0 0 2mm 0'
      }}>
        ID: {dp.id}
      </p>
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 1.5mm 0',
        lineHeight: '1.2'
      }}>
        {dp.recipient_name}
      </p>
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 1.5mm 0',
          lineHeight: '1.2'
        }}>
          {dp.product}
        </p>
      )}
      {dp.district && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          fontWeight: '500',
          margin: '0 0 1.5mm 0',
          lineHeight: '1.2'
        }}>
          {dp.district}
        </p>
      )}
      <p style={{ 
        color: '#475569', 
        fontSize: '9px',
        margin: '0',
        paddingRight: '28mm',
        lineHeight: '1.3'
      }}>
        {dp.address}
      </p>
      <div style={{
        position: 'absolute',
        bottom: '4mm',
        right: '4mm',
        padding: '1.5mm',
        backgroundColor: 'white',
        borderRadius: '2mm'
      }}>
        <QRCodeCanvas
          value={dp.tracking_code}
          size={75}
          level="H"
          includeMargin={false}
        />
      </div>
    </div>
  );
}

// Provincia Label for A4
function ProvinciaLabelA4({ dp }) {
  return (
    <div
      style={{
        border: '2px solid #E6B43C',
        padding: '4mm',
        boxSizing: 'border-box',
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '3mm',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2mm'
      }}>
        <p style={{ 
          color: '#E6B43C', 
          fontSize: '18px', 
          fontWeight: 'bold',
          margin: '0'
        }}>
          ID: {dp.id}
        </p>
        <p style={{
          color: '#E6B43C',
          fontSize: '10px',
          fontWeight: 'bold',
          margin: '0'
        }}>
          PROVINCIA
        </p>
      </div>
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 1mm 0',
        lineHeight: '1.2'
      }}>
        {dp.recipient_name}
      </p>
      {dp.recipient_dni && (
        <p style={{ 
          color: '#475569', 
          fontSize: '9px',
          margin: '0 0 1.5mm 0'
        }}>
          DNI: {dp.recipient_dni}
        </p>
      )}
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '9px',
          margin: '0 0 1.5mm 0',
          lineHeight: '1.2'
        }}>
          {dp.product}
        </p>
      )}
      <p style={{ 
        color: '#475569', 
        fontSize: '10px',
        fontWeight: '600',
        margin: '0 0 1mm 0'
      }}>
        {dp.province} - {dp.district}
      </p>
      <p style={{ 
        color: '#475569', 
        fontSize: '8px',
        margin: '0 0 1mm 0',
        lineHeight: '1.2'
      }}>
        {dp.address}
      </p>
      <p style={{ 
        color: '#E6B43C', 
        fontSize: '8px',
        fontWeight: '600',
        margin: '0',
        lineHeight: '1.2'
      }}>
        Agencia: {dp.agency_address}
      </p>
    </div>
  );
}

// Thermal Print Layout Component
function ThermalPrintLayout({ limaDeliveries, provinciaDeliveries }) {
  const allDeliveries = [
    ...limaDeliveries.map(d => ({ ...d, type: 'lima' })),
    ...provinciaDeliveries.map(d => ({ ...d, type: 'provincia' }))
  ];

  return (
    <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {allDeliveries.map((dp, index) => (
        dp.type === 'lima' ? (
          <LimaLabelThermal 
            key={`lima-${dp.id}`} 
            dp={dp} 
            isLast={index === allDeliveries.length - 1}
          />
        ) : (
          <ProvinciaLabelThermal 
            key={`provincia-${dp.id}`} 
            dp={dp}
            isLast={index === allDeliveries.length - 1}
          />
        )
      ))}
    </div>
  );
}

// Lima Label for Thermal
function LimaLabelThermal({ dp, isLast }) {
  return (
    <div
      style={{
        width: '80mm',
        minHeight: '60mm',
        border: '2px solid #B1336E',
        padding: '4mm',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        marginBottom: '5mm',
        pageBreakAfter: isLast ? 'auto' : 'always',
        pageBreakInside: 'avoid',
        borderRadius: '3mm'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '3mm' }}>
        <QRCodeCanvas
          value={dp.tracking_code}
          size={80}
          level="H"
          includeMargin={false}
        />
      </div>
      <p style={{ 
        color: '#B1336E', 
        fontSize: '18px', 
        fontWeight: 'bold',
        margin: '0 0 2mm 0',
        textAlign: 'center'
      }}>
        ID: {dp.id}
      </p>
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 2mm 0'
      }}>
        {dp.recipient_name}
      </p>
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 1mm 0'
        }}>
          {dp.product}
        </p>
      )}
      {dp.district && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          fontWeight: '500',
          margin: '0 0 1mm 0'
        }}>
          {dp.district}
        </p>
      )}
      <p style={{ 
        color: '#475569', 
        fontSize: '9px',
        margin: '0'
      }}>
        {dp.address}
      </p>
    </div>
  );
}

// Provincia Label for Thermal
function ProvinciaLabelThermal({ dp, isLast }) {
  return (
    <div
      style={{
        width: '80mm',
        minHeight: '70mm',
        border: '2px solid #E6B43C',
        padding: '4mm',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        marginBottom: '5mm',
        pageBreakAfter: isLast ? 'auto' : 'always',
        pageBreakInside: 'avoid',
        borderRadius: '3mm'
      }}
    >
      <div style={{
        textAlign: 'center',
        marginBottom: '3mm',
        borderBottom: '1px solid #E6B43C',
        paddingBottom: '2mm'
      }}>
        <p style={{
          color: '#E6B43C',
          fontSize: '14px',
          fontWeight: 'bold',
          margin: '0 0 1mm 0'
        }}>
          ENVÍO A PROVINCIA
        </p>
        <p style={{ 
          color: '#E6B43C', 
          fontSize: '18px', 
          fontWeight: 'bold',
          margin: '0'
        }}>
          ID: {dp.id}
        </p>
      </div>
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 1mm 0'
      }}>
        {dp.recipient_name}
      </p>
      {dp.recipient_dni && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 2mm 0'
        }}>
          DNI: {dp.recipient_dni}
        </p>
      )}
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 2mm 0'
        }}>
          {dp.product}
        </p>
      )}
      <p style={{ 
        color: '#1e293b', 
        fontSize: '11px',
        fontWeight: '600',
        margin: '0 0 1mm 0'
      }}>
        {dp.province} - {dp.district}
      </p>
      <p style={{ 
        color: '#475569', 
        fontSize: '9px',
        margin: '0 0 2mm 0'
      }}>
        {dp.address}
      </p>
      <div style={{
        backgroundColor: '#FFF9E6',
        padding: '2mm',
        borderRadius: '2mm',
        border: '1px solid #E6B43C'
      }}>
        <p style={{ 
          color: '#E6B43C', 
          fontSize: '8px',
          fontWeight: '600',
          margin: '0',
          lineHeight: '1.3'
        }}>
          Agencia: {dp.agency_address}
        </p>
      </div>
    </div>
  );
}

export default PrintDeliveries;